import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDate } from '../utils/dateFormat';
import './UserManagement.css';

const ROLE_COLORS = {
  root: '#ef4444',
  admin: '#f59e0b',
  moderator: '#3b82f6',
  viewer: '#6b7280'
};

ROLE_COLORS.owner = '#10b981';

const LOCAL_PERMISSION_LABEL_KEYS = {
  'players.view': 'permissions.playersView',
  'players.edit': 'permissions.playersEdit',
  'players.delete': 'permissions.playersDelete',
  'phones.rename': 'permissions.phonesRename',
  'phones.private_messages.view': 'permissions.phonesPrivateMessagesView',
  'economy.view': 'permissions.economyView',
  'economy.edit': 'permissions.economyEdit',
  'items.view': 'permissions.itemsView',
  'items.edit': 'permissions.itemsEdit',
  'refund.view': 'permissions.refundView',
  'refund.edit': 'permissions.refundEdit',
  'refund.refresh': 'permissions.refundRefresh',
  'jobs.view': 'permissions.jobsView',
  'jobs.edit': 'permissions.jobsEdit',
  'jobs.catalog.view': 'permissions.jobsCatalogView',
  'jobs.catalog.edit': 'permissions.jobsCatalogEdit',
  'vehicles.view': 'permissions.vehiclesView',
  'vehicles.edit': 'permissions.vehiclesEdit',
  'donate.view': 'permissions.donateView',
  'codes.generate': 'permissions.codesGenerate',
  'codes.manage': 'permissions.codesManage',
  'coins.manage': 'permissions.coinsManage',
  'characters.view': 'permissions.charactersView',
  'characters.edit': 'permissions.charactersEdit',
  'characters.move': 'permissions.charactersMove',
  'characters.delete': 'permissions.charactersDelete',
  'users.view': 'permissions.usersView',
  'users.create': 'permissions.usersCreate',
  'users.edit': 'permissions.usersEdit',
  'users.delete': 'permissions.usersDelete',
  'audit.view': 'permissions.auditView'
};

function toTitleCase(value) {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function humanizePermission(permission, labels) {
  if (labels[permission]) {
    return labels[permission];
  }

  if (permission.endsWith('.*')) {
    const area = permission.slice(0, -2);
    return `${area.charAt(0).toUpperCase() + area.slice(1)}: full access`;
  }

  const [area, action] = permission.split('.');
  if (!area || !action) {
    return permission;
  }

  return `${action.charAt(0).toUpperCase() + action.slice(1)} ${area}`;
}

function normalizePermissionArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') {
    return Object.keys(value).filter((key) => value[key] === true);
  }
  return [];
}

function UserManagement() {
  const { apiFetch, user, hasRole, hasPermission } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [permissionPresets, setPermissionPresets] = useState({});
  const [permissionLabels, setPermissionLabels] = useState({});
  const [permissionGroups, setPermissionGroups] = useState({});
  const [selectedPreset, setSelectedPreset] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    display_name: '',
    email: '',
    discord_id: '',
    role: 'viewer',
    permissions: []
  });


  // Only allow assigning roles at or below your own (except root, which is always disabled unless you are root)
  const getAssignableRoles = () => {
    if (!user) return [];
    const userRole = user.role;
    if (userRole === 'root') {
      return [
        { value: 'root' },
        { value: 'admin' },
        { value: 'owner' },
        { value: 'moderator' },
        { value: 'viewer' }
      ];
    }
    if (userRole === 'admin') {
      return [
        { value: 'owner', disabled: true },
        { value: 'moderator' },
        { value: 'viewer' }
      ];
    }
    if (userRole === 'owner') {
      // Show owner as disabled to indicate their own rank
      return [
        { value: 'owner', disabled: true, note: t('roles.ownerLabel') + ' (' + t('users.youAreHere') + ')' },
        { value: 'moderator' },
        { value: 'viewer' }
      ];
    }
    if (userRole === 'moderator') {
      return [
        { value: 'moderator', disabled: true },
        { value: 'viewer' }
      ];
    }
    // viewer or unknown
    return [
      { value: 'viewer', disabled: true }
    ];
  };
  const roles = getAssignableRoles();

  const roleMeta = useMemo(() => ({
    root: { label: t('roles.rootLabel'), color: ROLE_COLORS.root, description: t('roles.rootDesc') },
    admin: { label: t('roles.adminLabel'), color: ROLE_COLORS.admin, description: t('roles.adminDesc') },
    moderator: { label: t('roles.moderatorLabel'), color: ROLE_COLORS.moderator, description: t('roles.moderatorDesc') },
    viewer: { label: t('roles.viewerLabel'), color: ROLE_COLORS.viewer, description: t('roles.viewerDesc') },
    owner: { label: t('roles.ownerLabel') || 'Owner', color: ROLE_COLORS.owner, description: t('roles.ownerDesc') || 'Site owner with admin permissions for their site' }
  }), [t]);

  const localPermissionLabels = useMemo(() => {
    return Object.fromEntries(
      Object.entries(LOCAL_PERMISSION_LABEL_KEYS).map(([permission, key]) => [permission, t(key)])
    );
  }, [t]);

  useEffect(() => {
    fetchPermissionMetadata();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedPermissionConfig = useMemo(() => {
    if (permissionGroups && Object.keys(permissionGroups).length > 0) {
      return Object.entries(permissionGroups)
        .filter(([, group]) => Array.isArray(group.permissions) && group.permissions.length > 0)
        .map(([key, group]) => ({
          key,
          label: group.label || toTitleCase(key),
          icon: group.icon || key.charAt(0).toUpperCase(),
          permissions: group.permissions
        }));
    }

    const buckets = {};
    availablePermissions.forEach((permission) => {
      const area = permission.split('.')[0] || 'other';
      if (!buckets[area]) {
        buckets[area] = [];
      }
      buckets[area].push(permission);
    });

    return Object.entries(buckets).map(([key, permissions]) => ({
      key,
      label: toTitleCase(key),
      icon: key.charAt(0).toUpperCase(),
      permissions
    }));
  }, [availablePermissions, permissionGroups]);

  const fetchPermissionMetadata = async () => {
    try {
      const response = await apiFetch('/api/permissions/metadata');
      if (!response.ok) {
        throw new Error(t('users.failedPermissionMetadata'));
      }
      const data = await response.json();
      const availablePermissions = Array.isArray(data.availablePermissions) ? data.availablePermissions : [];
      const normalizedPermissions = Array.from(new Set([...availablePermissions, 'phones.rename']));
      const normalizedGroups = { ...(data.groups || {}) };

      if (!normalizedGroups.phones) {
        normalizedGroups.phones = {
          label: 'Phones',
          icon: 'Ph',
          permissions: ['phones.rename']
        };
      } else {
        normalizedGroups.phones = {
          ...normalizedGroups.phones,
          permissions: Array.from(new Set([...(normalizedGroups.phones.permissions || []), 'phones.rename']))
        };
      }

      setAvailablePermissions(normalizedPermissions);
      setPermissionPresets(data.presets || {});
      setPermissionLabels({ ...localPermissionLabels, ...(data.labels || {}) });
      setPermissionGroups(normalizedGroups);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiFetch('/api/users');
      if (!response.ok) {
        throw new Error(t('users.failedUsers'));
      }
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      display_name: '',
      email: '',
      discord_id: '',
      role: 'viewer',
      permissions: []
    });
    setSelectedPreset('');
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
    resetForm();
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        displayName: formData.display_name,
        email: formData.email,
        discordId: formData.discord_id,
        role: formData.role,
        permissions: formData.permissions,
        permissionPreset: selectedPreset || null
      };

      // If the current user is a site owner and is logged into their site (not management),
      // create the user scoped to that site via the site-specific endpoint.
      let response;
      if (user && user.role === 'owner' && user.siteId && user.siteId !== 'default') {
        response = await apiFetch(`/api/sites/${encodeURIComponent(user.siteId)}/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await apiFetch('/api/users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('users.failedCreate'));
      }

      closeModal();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const payload = {
        displayName: formData.display_name,
        email: formData.email,
        discordId: formData.discord_id,
        role: formData.role,
        permissions: formData.permissions,
        permissionPreset: selectedPreset || null
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      const response = await apiFetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('users.failedUpdate'));
      }

      closeModal();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('users.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(`/api/users/${userId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('users.failedDelete'));
      }

      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const response = await apiFetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (!response.ok) {
        throw new Error(t('users.failedStatus'));
      }

      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      username: userToEdit.username,
      password: '',
      display_name: userToEdit.display_name || '',
      email: userToEdit.email || '',
      discord_id: userToEdit.discord_id || '',
      role: userToEdit.role,
      permissions: normalizePermissionArray(userToEdit.permissions)
    });
    setSelectedPreset('');
  };

  const applyPreset = (presetName) => {
    setSelectedPreset(presetName);
    if (!presetName || !permissionPresets[presetName]) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      permissions: [...permissionPresets[presetName]]
    }));
  };

  const applyRoleDefaultPreset = () => {
    if (permissionPresets[formData.role]) {
      applyPreset(formData.role);
    }
  };

  const togglePermission = (permission) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const togglePermissionGroup = (permissions, shouldEnable) => {
    setFormData((prev) => {
      const current = new Set(prev.permissions);
      permissions.forEach((permission) => {
        if (shouldEnable) {
          current.add(permission);
        } else {
          current.delete(permission);
        }
      });

      return {
        ...prev,
        permissions: Array.from(current)
      };
    });
  };

  const permissionSummary = (permissionsInput) => {
    const permissions = normalizePermissionArray(permissionsInput);
    if (permissions.length === 0) {
      return { preview: [], remaining: 0 };
    }

    const preview = permissions.slice(0, 2).map((permission) => humanizePermission(permission, permissionLabels));
    return {
      preview,
      remaining: Math.max(0, permissions.length - preview.length)
    };
  };

  const isModalOpen = Boolean(showCreateModal || editingUser);
  const modalTarget = typeof document !== 'undefined' ? document.body : null;
  const modalNode = (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingUser ? t('users.modal.editUser') : t('users.modal.createUser')}</h2>
          <button className="modal-close" onClick={closeModal}>x</button>
        </div>

        <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('users.modal.username')}</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                disabled={Boolean(editingUser)}
              />
              <small className="field-hint">{t('users.fieldHintLogin')}</small>
            </div>

            <div className="form-group">
              <label>{t('users.modal.password')} {editingUser ? t('users.modal.keepPassword') : ''}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
              />
            </div>

            <div className="form-group">
              <label>{t('users.modal.displayName')}</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('users.modal.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="form-group full-width">
              <label>{t('users.modal.discordId')}</label>
              <input
                type="text"
                value={formData.discord_id}
                onChange={(e) => setFormData({ ...formData, discord_id: e.target.value })}
                placeholder={t('users.modal.discordPlaceholder')}
              />
            </div>

            <div className="form-group full-width">
              <label>{t('users.modal.role')}</label>
              <div className="role-selector">
                {roles.map((role) => {
                  const roleInfo = roleMeta[role.value];
                  return (
                    <label
                      key={role.value}
                      className={`role-option ${formData.role === role.value ? 'selected' : ''} ${role.disabled ? 'disabled' : ''}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={formData.role === role.value}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        disabled={role.disabled || editingUser?.role === 'root'}
                      />
                      <span className="role-label" style={{ borderColor: roleInfo.color }}>
                        <strong>{roleInfo.label}</strong>
                        <small>{roleInfo.description}</small>
                        {role.note && (
                          <span className="role-note" style={{ color: '#888', fontStyle: 'italic', marginLeft: 8 }}>{role.note}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="form-group full-width">
              <div className="label-row">
                <label>{t('users.modal.permissionPreset')}</label>
                <button type="button" className="link-btn" onClick={applyRoleDefaultPreset}>
                  {t('users.modal.applyRoleDefault')}
                </button>
              </div>
              <select value={selectedPreset} onChange={(e) => applyPreset(e.target.value)}>
                <option value="">{t('users.modal.customSelection')}</option>
                {Object.keys(permissionPresets).map((preset) => (
                  <option key={preset} value={preset}>{toTitleCase(preset)}</option>
                ))}
              </select>
              <div className="preset-chips">
                {Object.keys(permissionPresets).map((preset) => (
                  <button
                    type="button"
                    key={preset}
                    className={`preset-chip ${selectedPreset === preset ? 'active' : ''}`}
                    onClick={() => applyPreset(preset)}
                  >
                    {toTitleCase(preset)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group full-width">
              <label>{t('users.modal.permissions', { count: formData.permissions.length })}</label>
              <div className="permission-groups">
                {groupedPermissionConfig.map((group) => {
                  const selectedInGroup = group.permissions.filter((permission) => formData.permissions.includes(permission)).length;
                  return (
                    <section key={group.key} className="permission-group">
                      <div className="permission-group-header">
                        <div className="permission-group-title">
                          <span className="group-icon">{group.icon}</span>
                          <div>
                            <h4>{group.label}</h4>
                            <p>{t('users.modal.selectedCount', { selected: selectedInGroup, total: group.permissions.length })}</p>
                          </div>
                        </div>
                        <div className="group-actions">
                          <button
                            type="button"
                            onClick={() => togglePermissionGroup(group.permissions, true)}
                            className="group-btn"
                          >
                            {t('users.modal.selectAll')}
                          </button>
                          <button
                            type="button"
                            onClick={() => togglePermissionGroup(group.permissions, false)}
                            className="group-btn"
                          >
                            {t('users.modal.clear')}
                          </button>
                        </div>
                      </div>

                      <div className="permissions-grid grouped">
                        {group.permissions.map((permission) => (
                          <label key={permission} className="permission-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission)}
                              onChange={() => togglePermission(permission)}
                            />
                            <span>
                              <strong>{humanizePermission(permission, permissionLabels)}</strong>
                              <small>{permission}</small>
                            </span>
                          </label>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={closeModal}>
              {t('users.modal.cancel')}
            </button>
            <button type="submit" className="submit-btn">
              {editingUser ? t('users.modal.saveChanges') : t('users.createUser')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (!hasPermission('users.view')) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <span className="denied-icon">LOCK</span>
          <h2>{t('users.accessDeniedTitle')}</h2>
          <p>{t('users.accessDeniedDesc')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-state">
          <span className="loading-spinner"></span>
          <p>{t('users.loadingUsers')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="management-header">
        <div className="header-content">
          <div className="header-icon">UM</div>
          <div>
            <h1>{t('users.title')}</h1>
            <p>{t('users.subtitle')}</p>
          </div>
        </div>
        <div className="header-controls">
          <button className="create-user-btn" onClick={() => setShowCreateModal(true)}>
            <span>+</span>
            {t('users.createUser')}
          </button>
          {hasPermission('refund.refresh') && (
            <button
              className="create-user-btn"
              onClick={async () => {
                if (!window.confirm(t('refund.refreshItems'))) return;
                try {
                  const resp = await apiFetch('/api/refund/refresh-cache', { method: 'POST' });
                  const data = await resp.json();
                  if (!resp.ok) throw new Error(data.error || 'Failed');
                  alert(t('refund.refreshSuccess', { fetched: data.fetched || 0 }));
                } catch (err) {
                  alert(t('refund.errorLoadingItems', { error: err.message }));
                }
              }}
            >
              {t('refund.refreshItems')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert error">
          <span className="alert-icon">!</span>
          <div>
            <strong>{t('users.error')}</strong>
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)} className="close-alert">x</button>
        </div>
      )}

      <div className="user-stats">
        <div className="stat-box">
          <span className="stat-icon">{t('users.table.user')}</span>
          <div className="stat-content">
            <div className="stat-value">{users.length}</div>
            <div className="stat-label">{t('users.totalAccounts')}</div>
          </div>
        </div>
        <div className="stat-box">
          <span className="stat-icon">{t('users.enabled')}</span>
          <div className="stat-content">
            <div className="stat-value">{users.filter((u) => u.is_active).length}</div>
            <div className="stat-label">{t('users.enabled')}</div>
          </div>
        </div>
        <div className="stat-box">
          <span className="stat-icon">{t('users.idle')}</span>
          <div className="stat-content">
            <div className="stat-value">{users.filter((u) => !u.is_active).length}</div>
            <div className="stat-label">{t('users.disabled')}</div>
          </div>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('users.table.user')}</th>
              <th>{t('users.table.role')}</th>
              <th>{t('users.table.discord')}</th>
              <th>{t('users.table.scope')}</th>
              <th>{t('users.table.status')}</th>
              <th>{t('users.table.created')}</th>
              <th>{t('users.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const summary = permissionSummary(u.permissions);
              const roleInfo = roleMeta[u.role] || { label: u.role, color: '#9ca3af' };

              return (
                <tr key={u.id} className={!u.is_active ? 'inactive-row' : ''}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">{u.username.charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="user-username">{u.username}</div>
                        {u.display_name && <div className="user-display-name">{u.display_name}</div>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge role-${u.role}`}>
                      {roleInfo.label}
                    </span>
                  </td>
                  <td>
                    {u.discord_id ? (
                      <span className="discord-pill linked">{t('users.discordLinked')}</span>
                    ) : (
                      <span className="discord-pill unlinked">{t('users.discordUnlinked')}</span>
                    )}
                  </td>
                  <td>
                    <div className="permissions-cell detailed">
                      {summary.preview.length > 0 ? (
                        <>
                          {summary.preview.map((label) => (
                            <span className="permission-tag" key={`${u.id}-${label}`}>{label}</span>
                          ))}
                          {summary.remaining > 0 && <span className="permission-extra">{t('users.more', { count: summary.remaining })}</span>}
                        </>
                      ) : (
                        <span className="no-permissions">{t('users.noPermissions')}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      className={`status-toggle ${u.is_active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleActive(u.id, u.is_active)}
                      disabled={u.role === 'root'}
                    >
                      {u.is_active ? t('users.enabled') : t('users.disabled')}
                    </button>
                  </td>
                  <td>
                    <span className="date-text">{formatEuDate(u.created_at)}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit-btn" onClick={() => openEditModal(u)} title={t('users.editTitle')}>
                        {t('users.edit')}
                      </button>
                      {hasPermission('users.delete') && (u.role !== 'root' || hasRole('root')) && u.id !== user?.id && (
                        <button className="action-btn delete-btn" onClick={() => handleDeleteUser(u.id)} title={t('users.deleteTitle')}>
                          {t('users.delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isModalOpen && modalTarget && createPortal(modalNode, modalTarget)}
    </div>
  );
}

export default UserManagement;
